import { useState } from 'react';
import {
  Alert, Anchor, Button, Card, Center, Divider, PasswordInput, Stack, Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { IconAlertCircle, IconBrandAndroid } from '@tabler/icons-react';

import { ApiRequestError, login, register } from '../api/client';
import { Logo } from '../components/Logo';

/** Served from web/public/downloads via the api WEB_DIR SPA. */
const ANDROID_APK_HREF = '/downloads/seidhi-android.apk';

export function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const form = useForm({
    initialValues: { email: '', password: '', name: '', org_name: '' },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email address'),
      password: (v) =>
        mode === 'register' && v.length < 12
          ? 'Use at least 12 characters — length beats symbols'
          : null,
      org_name: (v) => (mode === 'register' && !v ? 'Required' : null),
    },
  });

  async function submit(values: typeof form.values) {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await login(values.email, values.password);
      } else {
        await register(values);
      }
      navigate('/', { replace: true });
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === 'registration_closed') {
        setError('This instance already has an account. Ask an owner to invite you.');
        setMode('login');
      } else {
        setError(e instanceof Error ? e.message : 'Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Center h="100vh" px="md">
      <Stack w={400} gap="md">
        <Stack gap={2} align="center">
          <Logo variant="lockup" size={40} wordmarkSize={28} />
        </Stack>

        <Card>
          <form onSubmit={form.onSubmit(submit)}>
            <Stack>
              {error && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
                  {error}
                </Alert>
              )}

              {mode === 'register' && (
                <>
                  <TextInput label="Your name" {...form.getInputProps('name')} />
                  <TextInput
                    label="Organization"
                    placeholder="Acme Ltd"
                    {...form.getInputProps('org_name')}
                  />
                </>
              )}

              <TextInput
                label="Email"
                autoComplete="username"
                data-autofocus
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label="Password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                {...form.getInputProps('password')}
              />

              <Button type="submit" loading={busy} fullWidth mt="xs">
                {mode === 'login' ? 'Sign in' : 'Create account'}
              </Button>

              <Divider
                label={mode === 'login' ? 'First time here?' : 'Already have an account?'}
                labelPosition="center"
              />
              <Anchor
                size="sm"
                ta="center"
                onClick={() => {
                  setMode(mode === 'login' ? 'register' : 'login');
                  setError(null);
                }}
              >
                {mode === 'login' ? 'Set up the first account' : 'Sign in instead'}
              </Anchor>
            </Stack>
          </form>
        </Card>

        {mode === 'register' && (
          <Text size="xs" c="dimmed" ta="center">
            Registration is open only until the first account exists. After that,
            accounts arrive by invitation from an owner.
          </Text>
        )}

        <Card withBorder padding="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Try push on Android
            </Text>
            <Text size="xs" c="dimmed">
              Install the sample APK, identify a profile, enable push, then send
              from the dashboard. Allow unknown apps if your phone asks.
            </Text>
            <Button
              component="a"
              href={ANDROID_APK_HREF}
              download="seidhi-android.apk"
              variant="light"
              leftSection={<IconBrandAndroid size={16} />}
              fullWidth
            >
              Download Android APK
            </Button>
          </Stack>
        </Card>
      </Stack>
    </Center>
  );
}
